import TextData from 'domain/entity/widgets/TextData';
import GaugeData from 'domain/entity/widgets/GaugeData';
import ChartData from 'domain/entity/widgets/ChartData';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import SingleKPIData from 'domain/entity/widgets/SingleKPIData';
import { WidgetOptionConfigurationField, WidgetType } from 'domain/entity/widget';
import { asWidgetChartLimit, generateChartLimitSeries, JsonChartLimit } from 'adapter/out/client/widgets/data/mappers/common';
import { asWidgetGaugeRanges, JsonGaugeRange } from 'adapter/out/client/widgets/data/mappers/GaugeMapper';

const getRangesOptions = (options: WidgetOptionConfigurationField[], data: GaugeData) =>
    options.filter((option) => option.key === OPTIONS.RANGES).length > 0
        ? asWidgetGaugeRanges(options.filter((option) => option.key === OPTIONS.RANGES)[0].value as JsonGaugeRange[])
        : data.ranges;

const getCheckpointsOptions = (options: WidgetOptionConfigurationField[], data: GaugeData) =>
    options.filter((option) => option.key === OPTIONS.CHECKPOINTS).length > 0
        ? asWidgetChartLimit(options.filter((option) => option.key === OPTIONS.CHECKPOINTS)[0].value as JsonChartLimit[])
        : data.checkpoints;

const getChartLimitOptions = (options: WidgetOptionConfigurationField[], data: ChartData) =>
    options.filter((option) => option.key === OPTIONS.CHECKPOINTS).length > 0
        ? generateChartLimitSeries(
              asWidgetChartLimit(options.filter((option) => option.key === OPTIONS.CHECKPOINTS)[0].value as JsonChartLimit[]),
              data.axesX[0].key,
              data.axesX,
              data.axesY[0].key
          )
        : [];

const getMaxValueOption = (customMax: WidgetOptionConfigurationField, data: GaugeData) => (customMax ? Number(customMax.value) : data.max);
const getMinValueOption = (customMin: WidgetOptionConfigurationField, data: GaugeData) => (customMin ? Number(customMin.value) : data.min);
const getShowAsPercentageValueOption = (
    showAsPercentage: WidgetOptionConfigurationField,
    type: WidgetType,
    data: WidgetDataTypes | Record<string, never>
) => {
    const comparison = type === WidgetType.GAUGE ? (data as GaugeData).comparison : (data as SingleKPIData).comparison;
    return showAsPercentage ? showAsPercentage.value !== ABSOLUTE_PERCENTAGE_VALUES.ABSOLUTE : comparison.showAsPercentage;
};

const getAlignTextOption = (options: WidgetOptionConfigurationField[], name: string) =>
    options.filter((option) => option.key === `${OPTIONS.ALIGN_TEXT}_${name}`).length > 0
        ? (options.filter((option) => option.key === `${OPTIONS.ALIGN_TEXT}_${name}`)[0].value as ALIGN_TEXT)
        : ALIGN_TEXT.START;

export const applyOptions = (data: WidgetDataTypes | Record<string, never>, optionFields: WidgetOptionConfigurationField[]) => {
    const { type } = data;
    if (optionFields.length === 0 || (data && Object.keys(data).length === 0 && data.constructor === Object)) return data;
    const customTitle = optionFields.filter((option) => option.key === OPTIONS.CUSTOM_TITLE)[0];
    const title = customTitle ? customTitle.value : data.description.title;
    const customMin = optionFields.filter((option) => option.key === OPTIONS.MIN)[0];
    const min = type === WidgetType.GAUGE ? getMinValueOption(customMin, data as GaugeData) : null;
    const customMax = optionFields.filter((option) => option.key === OPTIONS.MAX)[0];
    const max = type === WidgetType.GAUGE ? getMaxValueOption(customMax, data as GaugeData) : null;
    const ranges = type === WidgetType.GAUGE ? getRangesOptions(optionFields, data as GaugeData) : [];
    const checkpoints = type === WidgetType.GAUGE ? getCheckpointsOptions(optionFields, data as GaugeData) : [];
    const chartLimits =
        type === WidgetType.BAR || type === WidgetType.LINE || type === WidgetType.STACKED_BAR
            ? getChartLimitOptions(optionFields, data as ChartData)
            : [];
    const absolutePercentage = optionFields.filter((option) => option.key === OPTIONS.ABSOLUTE_PERCENTAGE)[0];
    const comparisonShowPercentage =
        type === WidgetType.GAUGE || type === WidgetType.SINGLE_KPI
            ? getShowAsPercentageValueOption(absolutePercentage, type, data)
            : false;
    const alignTextHeader = getAlignTextOption(optionFields, 'header');
    const alignTextBody = getAlignTextOption(optionFields, 'body');

    switch (type) {
        case WidgetType.TEXT: {
            const textData = data as TextData;
            return {
                ...textData,
                header: {
                    ...textData.header,
                    alignText: alignTextHeader,
                },
                body: {
                    ...textData.body,
                    alignText: alignTextBody,
                },
            };
        }
        case WidgetType.GAUGE: {
            const gaugeData = data as GaugeData;
            return {
                ...gaugeData,
                description: {
                    ...data.description,
                    title,
                },
                comparison: {
                    ...gaugeData.comparison,
                    showAsPercentage: comparisonShowPercentage,
                },
                ranges,
                checkpoints,
                min,
                max,
            };
        }
        case WidgetType.BAR:
        case WidgetType.LINE:
        case WidgetType.STACKED_BAR: {
            const chartData = data as ChartData;
            return {
                ...chartData,
                description: {
                    ...data.description,
                    title,
                },
                series: [...chartData.series, ...chartLimits],
            };
        }
        case WidgetType.SINGLE_KPI: {
            const singleKpiData = data as SingleKPIData;
            return {
                ...data,
                description: {
                    ...singleKpiData.description,
                    title,
                },
                comparison: {
                    ...singleKpiData.comparison,
                    showAsPercentage: comparisonShowPercentage,
                },
            };
        }
        case WidgetType.PIE:
        case WidgetType.HEATMAP:
        default:
            return {
                ...data,
                description: {
                    ...data.description,
                    title,
                },
            };
    }
};

export enum ABSOLUTE_PERCENTAGE_VALUES {
    ABSOLUTE = 'absolute',
    PERCENTAGE = 'percentage',
}

export enum OPTIONS {
    CUSTOM_TITLE = 'title',
    RANGES = 'ranges',
    CHECKPOINTS = 'checkpoints',
    MIN = 'min',
    MAX = 'max',
    ALIGN_TEXT = 'align_text',
    ABSOLUTE_PERCENTAGE = 'absolute_percentage',
}

export enum ALIGN_TEXT {
    START = 'start',
    CENTER = 'center',
    END = 'end',
    JUSTIFY = 'justify',
}
